import { useTranslation } from 'react-i18next'
import { currentLocale } from '../../i18n'
import { formatFecha } from '../unidades/tipos'
import { duracion, hora, porDia, segundosVivos, enColacion } from './tiempo'

// Capsules grouped by day, newest first: time range, who / which parte,
// duration, overtime and how it ended. `mostrar` picks what each line names:
// 'persona' (in a parte), 'parte' (for a person) or 'ambos' (reports).
// `recibido`: Date.now() when the list arrived, for the running ones' timer;
// `ahora`: the ticking clock. onParte(id) / onPersona(id) make names links;
// onDetener(capsula) / onBorrar(capsula) add those buttons.
export default function ListaCapsulas({ caps, mostrar = 'ambos', recibido, ahora, onParte, onPersona, onDetener, onBorrar }) {
  const { t } = useTranslation('engineering')
  if (!caps.length) return <p className="text-gray-400 text-sm text-center py-8">{t('capsulas.sinCapsulas')}</p>
  return (
    <div className="space-y-4">
      {porDia(caps).map(d => (
        <section key={d.dia}>
          <div className="flex items-baseline justify-between border-b border-gray-100 pb-1 mb-1.5">
            <h4 className="text-xs font-semibold text-gray-700 capitalize">
              {new Date(`${d.dia}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long' })} {formatFecha(d.dia)}
            </h4>
            <span className="text-xs text-gray-500">{duracion(d.caps.reduce((s, c) => s + segundosVivos(c, recibido, ahora), 0))}</span>
          </div>
          <ul className="space-y-1">
            {d.caps.map(c => (
              <li key={c.id} className={`flex items-start gap-3 rounded-lg px-2.5 py-1.5 text-sm ${
                c.abierta ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-gray-50'}`}>
                <span className="font-mono text-xs text-gray-500 pt-0.5 w-[92px] shrink-0">
                  {hora(c.inicio)}–{c.abierta ? t('capsulas.ahora') : hora(c.fin)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2">
                    {mostrar !== 'parte' && (
                      <Nombre onClick={onPersona && (() => onPersona(c.persona_id))}>{c.persona_nombre}</Nombre>
                    )}
                    {mostrar === 'ambos' && <span className="text-gray-300">·</span>}
                    {mostrar !== 'persona' && (
                      <Nombre onClick={onParte && (() => onParte(c.parte_id))}>
                        #{c.parte_numero} <span className="font-normal text-gray-500">{c.unidad_codigo}</span>
                      </Nombre>
                    )}
                    {mostrar !== 'persona' && <span className="text-xs text-gray-500 truncate max-w-[220px]">{c.parte_nombre}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px]">
                    {c.hora_extra && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"
                        title={c.autorizada_por ? t('capsulas.autorizadaPor', { por: c.autorizada_por }) : undefined}>
                        {t('capsulas.horaExtra')}
                      </span>
                    )}
                    {enColacion(c, recibido, ahora)
                      ? <span className="text-gray-600 font-medium">◐ {t('capsulas.retomaCorto', { hora: hora(c.inicio) })}</span>
                      : c.abierta
                      ? <span className="text-emerald-700 font-medium">● {t('capsulas.enCurso')}</span>
                      : c.motivo_fin && <span className="text-gray-400">{t(`capsulas.motivos.${c.motivo_fin}`)}</span>}
                    {c.nota && <span className="text-gray-500 italic truncate max-w-[260px]">“{c.nota}”</span>}
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-800 tabular-nums shrink-0">{duracion(segundosVivos(c, recibido, ahora))}</span>
                {c.abierta && onDetener && (
                  <button type="button" onClick={() => onDetener(c)}
                    className="text-xs font-medium text-gray-500 hover:text-gray-900 shrink-0">{t('capsulas.pausar')}</button>
                )}
                {!c.abierta && onBorrar && (
                  <button type="button" onClick={() => onBorrar(c)} title={t('capsulas.borrar')}
                    className="text-gray-300 hover:text-red-500 shrink-0">×</button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function Nombre({ onClick, children }) {
  if (!onClick) return <span className="font-medium text-gray-900">{children}</span>
  return (
    <button type="button" onClick={onClick} className="font-medium text-gray-900 hover:text-indigo-600 hover:underline text-left">
      {children}
    </button>
  )
}
